/**
 * Extract domain from a URL
 * @param {string} url - URL string
 * @returns {string} Domain name
 */
export const extractDomain = (url) => {
  if (!url) return ''
  
  try {
    const domain = new URL(url).hostname
    return domain.replace('www.', '')
  } catch (e) {
    return url
  }
}

/**
 * Check if a string is a valid URL
 * @param {string} str - String to check
 * @returns {boolean} Whether the string is a valid URL
 */
export const isValidUrl = (str) => {
  if (!str) return false
  
  try {
    new URL(str)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Add https protocol to URL if missing
 * @param {string} url - URL string
 * @returns {string} URL with protocol
 */
export const ensureHttps = (url) => {
  if (!url) return ''
  
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`
  }
  
  return url
}

/**
 * Check if URL is an image URL
 * @param {string} url - URL to check
 * @returns {boolean} Whether the URL is an image
 */
export const isImageUrl = (url) => {
  if (!url) return false
  
  const link = url.toLowerCase()

  // Check image extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
  const hasImageExtension = imageExtensions.some(ext => link.endsWith(ext))

  // Check image paths
  const isImagePath = link.includes('/image/') ||
      link.includes('/images/') ||
      link.includes('/cdn/image/')

  return hasImageExtension || isImagePath
}

/**
 * Check if URL is a video URL
 * @param {string} url - URL to check
 * @returns {boolean} Whether the URL is a video
 */
export const isVideoUrl = (url) => {
  if (!url) return false
  
  const link = url.toLowerCase()

  // Check video extensions
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv']
  const hasVideoExtension = videoExtensions.some(ext => link.endsWith(ext))

  // Check video hosting services
  const isVideoHosting = link.includes('youtube.com/watch') ||
      link.includes('youtu.be/') ||
      link.includes('vimeo.com/') ||
      link.includes('/video/') ||
      link.includes('/videos/') ||
      link.includes('tiktok.com')

  return hasVideoExtension || isVideoHosting
}

// Export all functions as a named object
export const urlUtils = {
  extractDomain,
  isValidUrl,
  ensureHttps,
  isImageUrl,
  isVideoUrl
}
